/**
 * The list's governed-action SEAM (#1131 ruling point 2).
 *
 * ── why this is a module and not four lines inside ListView ────────────────
 * `ListView` is the console's busiest file and #1982 is still building on it:
 * `packages/experience/src/tablePresentation.ts` already declares
 * `TableCardSpec.actions?: string[]` — *"stable governed action ids/
 * capabilities, never client callbacks"* — with nothing yet resolving it, and
 * `GridColumn.compact: 'actions'` already exists with nobody supplying it.
 *
 * Those two are the card presentation's half of this same idea. If the acts
 * were welded into `ListView`'s column mapping, that lane would have to reach
 * into the middle of a 1300-line component to place the same buttons in a card
 * — or, far more likely, grow a second way to render an act. So the placement
 * lives here, behind one hook, and `ListView` holds three one-liners.
 *
 * The contract a later presentation needs is exactly what is returned below:
 * *does this entity have per-record acts*, *does it have selection acts*, the
 * column when a table wants one, and the selection strip's children. A card
 * layout uses the first and the last and ignores the column.
 *
 * ── what deliberately stays out ────────────────────────────────────────────
 * No authority, no filtering by status, no label mapping. The acts are whatever
 * `core.my_entity_actions` named for this caller; this file only decides WHERE
 * they hang.
 */
import type { ReactNode } from 'react'
import type { GridColumn } from '@solum/ui'
import type { Row } from '@solum/sdk'
import { useEntityActions } from '../data/queries'
import { BatchActions, RowActions } from '../record/EntityActions'

/** The trailing column's key. Not a field: it never enters a view document. */
export const ACTION_COLUMN_KEY = '__actions'

export interface ListActionSeam {
  /** The entity has at least one per-record act. */
  hasRow: boolean
  /** The entity has at least one selection act. */
  hasBatch: boolean
  /** Stable governed tool ids and their server-authored labels for card recipes. */
  choices: { key: string; label: string }[]
  /**
   * The COLLECTION-level acts (`cardinality = 'none'`), for a table's command
   * list (#2375). Separate from `choices`: a command acts on the collection
   * with no selection, and the trust boundary refuses any other binding.
   */
  commandChoices: { key: string; label: string }[]
  /**
   * The trailing grid column, or null when there is nothing to hang.
   *
   * A TRAILING column rather than a fourth lead control: `DataGrid`'s lead
   * column is budgeted for at most three (expand, select, pin) and a fourth
   * needs a new width token. It is appended AFTER the view's own columns and
   * never added to the view document, so it reaches neither the column chooser,
   * nor a saved view, nor a solution bundle — it is chrome the surface owns.
   */
  column: GridColumn | null
  /** The same row surface narrowed by a card declaration at render time. */
  columnFor: (allowedTools?: readonly string[]) => GridColumn | null
  /** The selection acts, for whichever surface owns the command strip. */
  renderBatch: (entityIds: string[]) => ReactNode
}

/* THE ⋮ AND THE CELL AROUND IT (#2406 rogue census, control-clipped). This was
   a flat 48px, and the cell pads each side by --sp-3: on a coarse pointer the
   trigger takes its 44px floor, and 44 + 24 does not fit in 48, so the cell's
   clip cut the menu button on every phone row (/crm, /e/crm_opportunity). The
   track is the control at the pointer's floor plus the cell's own padding. */
const ACTION_COLUMN_WIDTH = 'calc(max(var(--ctl-h), var(--ctl-h-floor)) + var(--sp-6))'

export function useListActionSeam(entity: string): ListActionSeam {
  /* Asked ONCE for the whole list, with no record id: it answers only WHETHER
     this entity has acts. Whether THIS caller may take one on a given row is a
     per-record question the row menu asks when it opens — 50 authority
     questions to draw 50 overflow buttons would throw 49 answers away. */
  const listActions = useEntityActions(entity)
  const acts = listActions.data ?? []
  const hasRow = acts.some((a) => a.cardinality !== 'batch')
  const hasBatch = acts.some((a) => a.cardinality === 'batch')

  const columnFor = (allowedTools?: readonly string[]): GridColumn | null => {
    const allowed = allowedTools == null
      ? acts
      : acts.filter((action) => allowedTools.includes(action.tool))
    if (!allowed.some((action) => action.cardinality !== 'batch')) return null
    return {
      key: ACTION_COLUMN_KEY, label: 'Actions', width: ACTION_COLUMN_WIDTH, align: 'right', sort: 'none',
      sortable: false, menuable: false, compact: 'actions',
      cell: (row: Row) => <RowActions entity={entity} id={String(row.id)} allowedTools={allowedTools} />,
    } as GridColumn
  }

  return {
    hasRow,
    hasBatch,
    choices: acts
      .filter((action) => action.cardinality !== 'batch')
      .map((action) => ({ key: action.tool, label: action.label })),
    commandChoices: acts
      .filter((action) => action.cardinality === 'none')
      .map((action) => ({ key: action.tool, label: action.label })),
    column: hasRow
      ? ({
        key: ACTION_COLUMN_KEY,
        label: 'Actions',
        width: ACTION_COLUMN_WIDTH,
        align: 'right',
        sort: 'none',
        /* Not a value, so it offers neither a sort nor a column menu. DataGrid
           states the rule on the props themselves — "an actions column has
           nothing to sort, and a sort button over one is an affordance that
           does nothing" — and ListView's own '__queue_pickup' column, the
           house precedent for a synthetic trailing column, sets both false.
           Offering a dead sort control inside the fix for lying affordances
           would be the defect wearing the cure's clothes. */
        sortable: false,
        menuable: false,
        /* The role #1982's card spec already reserves, so a card presentation
           places these without a second decision about what they are. */
        compact: 'actions',
        cell: (row: Row) => <RowActions entity={entity} id={String(row.id)} />,
      } as GridColumn)
      : null,
    columnFor,
    renderBatch: (entityIds: string[]) => <BatchActions entity={entity} ids={entityIds} />,
  }
}
